import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { money, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Layout";
import ProductForm from "@/components/ProductForm";
import Uploader from "@/components/Uploader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const STATUS_FR = { draft: "Brouillon", pending: "En attente", published: "Publié", rejected: "Refusé", archived: "Archivé" };
const STATUS_COLOR = { draft: "bg-neutral-100 text-neutral-600", pending: "bg-amber-50 text-amber-700", published: "bg-emerald-50 text-emerald-700", rejected: "bg-rose-50 text-rose-700", archived: "bg-neutral-100 text-neutral-400" };

function Stat({ label, value }) {
  return <div className="border border-neutral-100 p-5"><p className="over text-[0.55rem] text-neutral-400">{label}</p><p className="font-display text-3xl mt-1">{value}</p></div>;
}

export default function SellerDashboard() {
  const { user, ready, logout, refreshUser } = useAuth();
  const nav = useNavigate();
  const [seller, setSeller] = useState(null);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null); // product object or 'new'
  const [profile, setProfile] = useState({ universe: "", logo_url: "", portrait_url: "" });

  const reload = useCallback(async () => {
    const [s, st, p, o] = await Promise.all([
      api.get("/sellers/me"), api.get("/seller/stats"), api.get("/seller/products"), api.get("/seller/orders"),
    ]);
    setSeller(s.data.seller); setStats(st.data); setProducts(p.data.products); setOrders(o.data.orders);
    if (s.data.seller) setProfile({ universe: s.data.seller.universe || "", logo_url: s.data.seller.logo_url || "", portrait_url: s.data.seller.portrait_url || "" });
  }, []);

  useEffect(() => {
    if (ready && (!user || user.role !== "seller")) { nav(user ? "/devenir-createur" : "/connexion"); return; }
    if (user && user.role === "seller") reload().catch(() => {});
  }, [user, ready, nav, reload]);

  if (!user || user.role !== "seller" || !stats) return <div className="h-[50vh]" />;

  const saveProfile = async () => {
    try { await api.put("/sellers/me", profile); toast.success("Profil mis à jour"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const archive = async (id) => { await api.post(`/seller/products/${id}/archive`); toast.success("Archivé"); reload(); };
  const submit = async (id) => { try { await api.post(`/seller/products/${id}/submit`); toast.success("Soumis"); reload(); } catch (e) { toast.error(errMsg(e)); } };

  if (editing) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
        <button onClick={() => setEditing(null)} className="over text-[0.62rem] mb-6">← Retour</button>
        <h1 className="font-display text-4xl font-light mb-8">{editing === "new" ? "Nouvelle création" : "Modifier la création"}</h1>
        <ProductForm initial={editing === "new" ? null : editing} onSaved={() => { setEditing(null); reload(); }} onCancel={() => setEditing(null)} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="over text-[0.62rem] text-[#D4AF37]">Espace créateur</span>
          <h1 className="font-display text-4xl font-light mt-1">{seller?.brand_name}</h1>
        </div>
        <button onClick={logout} className="over text-[0.62rem] link-underline">Déconnexion</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat label="Chiffre d'affaires" value={money(stats.revenue)} />
        <Stat label="Commandes" value={stats.orders} />
        <Stat label="Produits publiés" value={stats.published} />
        <Stat label="En attente" value={stats.pending} />
      </div>

      {stats.low_stock?.length > 0 && (
        <div className="bg-rose-50 border border-rose-100 p-4 mb-8">
          <span className="over text-[0.58rem] text-rose-700">Stock faible</span>
          <p className="text-sm text-rose-700 mt-1">{stats.low_stock.map((l) => `${l.name} (${l.stock})`).join(" · ")}</p>
        </div>
      )}

      <Tabs defaultValue="products">
        <TabsList className="bg-transparent border-b border-neutral-100 rounded-none w-full justify-start gap-6 p-0 h-auto mb-8">
          {[["products", "Mes créations"], ["orders", "Commandes"], ["profile", "Mon univers"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="over text-[0.62rem] rounded-none border-b-2 border-transparent data-[state=active]:border-[#0A0A0A] data-[state=active]:shadow-none px-0 pb-3" data-testid={`seller-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="products">
          <div className="flex justify-end mb-5"><button onClick={() => setEditing("new")} className="btn btn-dark" data-testid="seller-add-product">+ Nouvelle création</button></div>
          <div className="space-y-3">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 border border-neutral-100 p-3" data-testid={`seller-product-${p.id}`}>
                <img src={p.images?.[0]} alt="" className="w-14 h-16 object-cover bg-neutral-100" />
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg leading-tight">{p.name}</p>
                  <p className="text-xs text-neutral-500">{p.subcategory} · Stock {p.total_stock} · {money(p.sale_price || p.price)}</p>
                  {p.reject_reason && <p className="text-xs text-rose-600 mt-1">Motif : {p.reject_reason}</p>}
                </div>
                <span className={`over text-[0.55rem] px-2 py-1 ${STATUS_COLOR[p.status]}`}>{STATUS_FR[p.status]}</span>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(p)} className="over text-[0.58rem] link-underline">Modifier</button>
                  {["draft", "rejected"].includes(p.status) && <button onClick={() => submit(p.id)} className="over text-[0.58rem] text-[#E0115F]">Soumettre</button>}
                  {p.status !== "archived" && <button onClick={() => archive(p.id)} className="over text-[0.58rem] text-neutral-400">Archiver</button>}
                </div>
              </div>
            ))}
            {products.length === 0 && <p className="text-sm text-neutral-500">Aucune création. Ajoutez votre première pièce.</p>}
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} className="border border-neutral-100 p-4">
                <div className="flex justify-between"><span className="font-mono text-xs text-neutral-400">#{o.id.slice(0, 8)}</span><span className="text-sm">{money(o.total)}</span></div>
                <p className="text-sm text-neutral-600 mt-1">{o.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</p>
              </div>
            ))}
            {orders.length === 0 && <p className="text-sm text-neutral-500">Aucune commande pour le moment.</p>}
          </div>
        </TabsContent>

        <TabsContent value="profile">
          <div className="max-w-xl space-y-5">
            <div>
              <label className="over text-[0.58rem] text-neutral-500">Logo (blanc sur fond transparent)</label>
              <div className="mt-2 bg-[#0A0A0A] p-4 inline-block"><Uploader value={profile.logo_url} onChange={(v) => setProfile({ ...profile, logo_url: v })} multiple={false} hint="Merci de fournir un logo blanc sur fond transparent." /></div>
            </div>
            <div>
              <label className="over text-[0.58rem] text-neutral-500">Portrait / visuel d'univers</label>
              <div className="mt-2"><Uploader value={profile.portrait_url} onChange={(v) => setProfile({ ...profile, portrait_url: v })} multiple={false} /></div>
            </div>
            <div>
              <label className="over text-[0.58rem] text-neutral-500">Présentation de mon univers</label>
              <textarea value={profile.universe} onChange={(e) => setProfile({ ...profile, universe: e.target.value })} className="w-full border border-neutral-200 px-3 py-2.5 text-sm mt-2 h-32" data-testid="seller-universe" />
            </div>
            <button onClick={saveProfile} className="btn btn-dark" data-testid="seller-profile-save">Enregistrer</button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
